import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'cognixis:motion-paused';
const CHANGE_EVENT = 'cognixis:motion-preference';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Keep the control usable when browser privacy settings prevent storage writes.
let memoryPreference: boolean | undefined;

const getMotionPaused = () => {
  if (typeof window === 'undefined') return false;
  if (memoryPreference !== undefined) return memoryPreference;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const subscribeToMotion = (onChange: () => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    memoryPreference = undefined;
    onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
};

const toggleMotion = () => {
  if (typeof window === 'undefined') return;
  const paused = !getMotionPaused();
  try {
    window.localStorage.setItem(STORAGE_KEY, String(paused));
    memoryPreference = undefined;
  } catch {
    memoryPreference = paused;
  }
  // Storage events only reach other tabs; notify mounted controls in this tab too.
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const getReducedMotion = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(REDUCED_MOTION_QUERY).matches;

const subscribeToReducedMotion = (onChange: () => void) => {
  if (typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(REDUCED_MOTION_QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
};

const getServerSnapshot = () => false;

/** One persisted motion choice across routes, with live operating-system support. */
export const useMotionPreference = () => {
  const motionPaused = useSyncExternalStore(subscribeToMotion, getMotionPaused, getServerSnapshot);
  const reducedMotion = useSyncExternalStore(subscribeToReducedMotion, getReducedMotion, getServerSnapshot);
  return { motionPaused, reducedMotion, motionDisabled: motionPaused || reducedMotion, toggleMotion };
};
